// "Dashboard" — ported from mantis-business-front-brinks's routes/reportes.tsx onto
// real data: `filterOffices` for the per-closing rows (same as PanelCierres.jsx), and ONE
// `getCierreAlertsBatch` over each office's LATEST closing (via `ultimoCierrePorOficina`) to count
// "documentos faltantes" with `faltantesDeDetalle`'s rule. It used to be one
// `getCierreClosingDetail` per office — 140 requests for Bancamía, and the page waited for all of
// them. "Cierres por periodo" and "Facturación al banco" load beside the rows, not after them.
//
// B-131 — the interactive Colombia map (`MapaOficinas.jsx`), placed only from what the registry
// declares: an office's DANE code or its declared city, matched against DANE's DIVIPOLA — never a
// position guessed from a name. The offices it cannot place are said under it.
//
// B-136 — one period for the whole page (Todo, Hoy, 7 días, 30 días, a month or a range, the range
// asked of the server), the map right under it as the page's filter (a municipality or one office:
// the figures, tables and the client's committee read only those offices until "Ver todas las
// oficinas"), one Excel (the committee's metrics workbook for a client that has one, the per-office
// report otherwise) and the committee's presentation. Long tables are paged, never scrolled inside
// the page. "Cierres por periodo", "Facturación" and "Tendencias" keep every office and their own controls.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Building2, ChevronLeft, ChevronRight, Download, FileX2, LineChart as LineIcon, Loader2, Map as MapIcon, MapPin, Presentation, Scale, ShieldAlert, ShieldCheck, X,
} from "lucide-react";
import { Button, Spinner, cn } from "@mantis-sas/ui";

import { selectAuthState } from "../../../store/selectors/authSelectors";
import { filterOffices, getCierreAlertsBatch, getCierreOperationReport, getExtractionFields, listCierreOffices } from "../../../ApiCalls/mantisBusiness";
import { conReintento } from "../../../utils/conReintento";
import {
  PRESETS_FECHA,
  columnasDeResumen,
  discrepanciaDeFila,
  enRangoDeFechas,
  estadoDeFila,
  faltantesDeDetalle,
  moneda,
  nombreOficinaDeMainKey,
  rangoDePreset,
  ultimoCierrePorOficina,
} from "../../../utils/cierrePanel";
import { getColombiaDateString } from "../../../utils/dateUtils";
import { MapaOficinas } from "./MapaOficinas";
import { useClienteCierres } from "./ClienteCierresContext";
import { useAccesoCierres } from "../../../hooks/useAccesoCierres";
import { PERMISO, filasExportables, puede } from "../../../utils/cierrePermisos";
import { FacturacionBanco } from "./FacturacionBanco";
import { TransaccionesReversas } from "./TransaccionesReversas";
import { TendenciasConciliacion } from "./TendenciasConciliacion";
import { CierreConciliacion } from "./CierreConciliacion";
import { descargarTablas } from "../../../utils/excelTabla";
import { VolumenCierres } from "./VolumenCierres";
import { ComiteSeguimiento } from "./ComiteSeguimiento";
import { CierresDeLaCifra } from "./CierresDeLaCifra";
import { EquipoCierres } from "./EquipoCierres";
import { usePaginaPorClave } from "../../../hooks/usePaginaPorClave";
import { hojasDeMetricas, pedidoDelComite } from "../../../utils/cierreComite";
import { descargarPresentacion } from "../../../utils/cierreComitePptx";
import "./styles/brinksTheme.css";
import { guia, guiaDe } from "../../../utils/cierreTours";

// The page's period: the dashboard's chips, plus a month and a range typed.
const PERIODOS = [...PRESETS_FECHA, { clave: "mes", etiqueta: "Mes" }, { clave: "personalizado", etiqueta: "Rango" }];
const POR_PAGINA = 15;
const CIUDADES_A_LA_VISTA = 8;
// "Todo" reads the newest closings; a period asks the server for its own, all of them.
const FILAS_TODO = 500;
const FILAS_PERIODO = 5000;

const ultimoDia = (mes) => {
  const [y, m] = mes.split("-").map(Number);
  return `${mes}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, "0")}`;
};

// B-131 — a row's difference is the client's own: the sum of its resumen columns (Banco Agrario's
// diferencia diaria, Bancamía's caja and bóveda — `columnasDeResumen`, as the panel reads them), in
// absolute pesos. Only a client with no resumen columns falls back to Davivienda's diferencia_cuadre.
function filaDeCierre(row, resumen = []) {
  const propia = resumen.length ? discrepanciaDeFila(row, resumen) : null;
  return {
    id: row.closing_id || `${row.codigo_oficina}-${row.fecha_cuadre}`,
    closingId: row.closing_id || null,
    codigo: row.codigo_oficina || "—",
    oficinaId: row.codigo_oficina || "—",
    oficina: nombreOficinaDeMainKey(row.main_key),
    ciudad: row.ciudad || "—",
    region: row.region || "—",
    fecha: row.fecha_cuadre || "",
    diferencia: resumen.length
      ? propia || 0
      : row.diferencia_cuadre != null && row.diferencia_cuadre !== "" ? Number(row.diferencia_cuadre) : 0,
    estado: estadoDeFila(row),
    // B-138 — the panel row itself, for the closing's detail opened from a figure.
    raw: row,
  };
}

function agrupar(lista, clave, etiqueta) {
  const mapa = new Map();
  lista.forEach((c) => {
    const k = clave(c);
    const actual = mapa.get(k) || { clave: k, etiqueta: etiqueta(c), total: 0, cuadre: 0, pendiente: 0, bloqueado: 0, discrepancia: 0 };
    actual.total += 1;
    actual[c.estado] += 1;
    actual.discrepancia += c.diferencia;
    mapa.set(k, actual);
  });
  return [...mapa.values()].sort((a, b) => b.bloqueado + b.pendiente - (a.bloqueado + a.pendiente) || b.total - a.total);
}

const COLOR_BARRA = { cuadre: "bg-brinks-success", pendiente: "bg-brinks-warning", bloqueado: "bg-brinks-danger" };

function Barra({ item, onAbrir }) {
  const pct = (n) => (item.total ? (n / item.total) * 100 : 0);
  const salud = Math.round(pct(item.cuadre));
  const Contenedor = onAbrir ? "button" : "div";
  return (
    <Contenedor
      {...(onAbrir ? { type: "button", onClick: () => onAbrir(item), title: `Ver los cierres de ${item.etiqueta}` } : {})}
      className={cn(
        "grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-2 py-1.5 text-left",
        onAbrir && "-mx-2 rounded-[8px] px-2 transition hover:bg-brinks-surface-hover"
      )}
    >
      <div className="min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-[12.5px] font-medium text-brinks-foreground">{item.etiqueta}</span>
          <span className="shrink-0 text-[11px] tabular-nums text-brinks-muted-foreground">{salud}% cuadra</span>
        </div>
        <div className="mt-1 flex h-1.5 w-full overflow-hidden rounded-full bg-brinks-surface">
          {["cuadre", "pendiente", "bloqueado"].map((estado) =>
            item[estado] ? <span key={estado} className={cn("h-full transition-all duration-300", COLOR_BARRA[estado])} style={{ width: `${pct(item[estado])}%` }} /> : null
          )}
        </div>
        <p className="mt-1 truncate text-[10.5px] text-brinks-muted-foreground tabular-nums">
          {item.total} cierres · dif. {moneda(item.discrepancia)}
        </p>
      </div>
      {onAbrir ? <ChevronRight className="h-3.5 w-3.5 shrink-0 text-brinks-muted-foreground" aria-hidden /> : null}
    </Contenedor>
  );
}

// One headline figure: icon, label, the figure, what it means, and its trend over the period's days.
// The `hero` card is the figure the page is about, in the primary's dark so the eye lands there.
// B-138 — with `onAbrir` the card is a button that opens the closings it counts.
function Indicador({ icono: Icono, etiqueta, valor, detalle, tendencia, hero, tono, onAbrir }) {
  const Contenedor = onAbrir ? "button" : "article";
  return (
    <Contenedor
      {...guiaDe(etiqueta, detalle)}
      {...(onAbrir ? { type: "button", onClick: onAbrir, "aria-label": `${etiqueta}: ${valor}. Ver los cierres` } : {})}
      className={cn(
        "group relative flex min-h-[124px] min-w-0 flex-col overflow-hidden rounded-[14px] border px-4 pb-2 pt-3.5 text-left shadow-brinks-card",
        hero ? "border-transparent bg-primary text-primary-foreground" : "bg-card",
        onAbrir && "cursor-pointer transition hover:-translate-y-px hover:shadow-[0_10px_24px_-12px_rgba(15,23,42,0.35)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
      )}
    >
      <div className="flex items-center gap-2">
        <span className={cn("grid h-7 w-7 shrink-0 place-items-center rounded-[8px]", hero ? "bg-white/10 text-white" : tono)}>
          <Icono className="h-[15px] w-[15px]" />
        </span>
        <p className={cn("truncate text-[12px] font-medium", hero ? "text-white/70" : "text-brinks-muted-foreground")}>{etiqueta}</p>
        {onAbrir ? (
          <span className={cn("ml-auto inline-flex shrink-0 items-center gap-0.5 text-[11px] font-medium opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100", hero ? "text-white/80" : "text-brinks-muted-foreground")}>
            Ver cierres <ChevronRight className="h-3 w-3" />
          </span>
        ) : null}
      </div>
      <p className={cn("mt-2 font-lora font-semibold leading-none tabular-nums", hero ? "text-[30px] text-white" : "text-[24px] text-brinks-foreground")}>
        {valor}
      </p>
      {detalle ? (
        <p className={cn("mt-1.5 text-[11.5px]", hero ? "text-white/65" : "text-brinks-muted-foreground")}>{detalle}</p>
      ) : null}
      {tendencia && tendencia.length > 1 ? (
        <div className="-mx-4 mt-auto h-9 pt-1.5" aria-hidden="true">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={tendencia} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
              <Area
                type="monotone"
                dataKey="valor"
                stroke={hero ? "#ffffff" : "hsl(var(--primary))"}
                strokeWidth={1.5}
                fill={hero ? "rgba(255,255,255,0.14)" : "rgba(15,23,42,0.07)"}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : null}
    </Contenedor>
  );
}

// A titled card. `cuerpo` replaces the padded body; `extra` sits at the right of the header.
function Grafica({ icono: Icono, titulo, subtitulo, extra, cuerpo, children }) {
  return (
    <section {...guiaDe(titulo, subtitulo)} className="flex min-w-0 flex-col overflow-hidden rounded-[14px] border bg-card shadow-brinks-card" aria-label={titulo}>
      <header className="flex flex-wrap items-start gap-3 border-b px-4 py-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-brinks-surface text-brinks-foreground">
          <Icono className="h-4 w-4" />
        </span>
        <div className="min-w-[min(100%,12rem)] flex-1">
          <h2 className="text-[14px] font-semibold text-brinks-foreground">{titulo}</h2>
          {subtitulo ? <p className="mt-0.5 text-[12px] text-brinks-muted-foreground">{subtitulo}</p> : null}
        </div>
        {extra ? <div className="shrink-0">{extra}</div> : null}
      </header>
      {cuerpo || <div className="px-3 py-3">{children}</div>}
    </section>
  );
}

// B-138 — a table row that opens what it stands for: a click, or Enter on the focused row.
const FILA_QUE_ABRE = "cursor-pointer hover:bg-brinks-surface-hover focus:bg-brinks-surface-hover focus:outline-none";
const filaQueAbre = (abrir, nombre) => ({
  tabIndex: 0,
  title: `Ver los cierres de ${nombre}`,
  onClick: abrir,
  onKeyDown: (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      abrir();
    }
  },
});

const Conteo = ({ n }) => (
  <span className="rounded-full bg-brinks-surface px-2 py-0.5 text-[11px] tabular-nums text-brinks-muted-foreground">{n}</span>
);

// A table's pager, inside its card: the page itself is the only thing that scrolls.
function Pagina({ pagina, total, onPagina }) {
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));
  if (paginas <= 1) return null;
  const desde = (pagina - 1) * POR_PAGINA + 1;
  return (
    <div className="flex items-center justify-between gap-2 border-t px-4 py-2 text-[11.5px] text-brinks-muted-foreground">
      <span className="tabular-nums">{desde}–{Math.min(total, desde + POR_PAGINA - 1)} de {total}</span>
      <span className="flex items-center gap-1">
        <button type="button" aria-label="Página anterior" disabled={pagina <= 1} onClick={() => onPagina(pagina - 1)} className="grid h-7 w-7 place-items-center rounded-[7px] border bg-card disabled:opacity-40">
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <span className="min-w-[48px] text-center tabular-nums">{pagina} / {paginas}</span>
        <button type="button" aria-label="Página siguiente" disabled={pagina >= paginas} onClick={() => onPagina(pagina + 1)} className="grid h-7 w-7 place-items-center rounded-[7px] border bg-card disabled:opacity-40">
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </span>
    </div>
  );
}

// The city or region list: the first ones, and the rest on request — never a box that scrolls.
function ListaDeGrupos({ grupos, onAbrir }) {
  const [todas, setTodas] = useState(false);
  const vista = todas ? grupos : grupos.slice(0, CIUDADES_A_LA_VISTA);
  return (
    <div className="px-4 py-1">
      <div className="divide-y">
        {vista.map((c) => <Barra key={c.clave} item={c} onAbrir={onAbrir} />)}
      </div>
      {grupos.length > CIUDADES_A_LA_VISTA ? (
        <button type="button" onClick={() => setTodas((v) => !v)} className="w-full border-t py-2 text-[12px] font-medium text-brinks-foreground hover:underline">
          {todas ? "Ver menos" : `Ver todas (${grupos.length})`}
        </button>
      ) : null}
    </div>
  );
}

function CierresReportes(props) {
  // B-55 — which client the shell's selector is on. Every row below is that client's.
  const { clientCode, listo, clientes } = useClienteCierres();
  // BR-38 — export is asked of this client, and writes only the rows whose office it reaches
  // (`filasExportables`): no route on the server sees an export, so this is the only check.
  const acceso = useAccesoCierres();
  const puedeExportar = puede(acceso, PERMISO.EXPORTAR, { cliente: clientCode });
  const nombreCliente = (clientes || []).find((c) => c.code === clientCode)?.name || clientCode;
  const [cargando, setCargando] = useState(true);
  const [todasLasFilas, setTodasLasFilas] = useState([]);
  const hoy = useMemo(() => getColombiaDateString(), []);
  // B-136 — the page's period: a chip, a month or a typed range.
  const [preset, setPreset] = useState("todo");
  const [mes, setMes] = useState(hoy.slice(0, 7));
  const [personalizado, setPersonalizado] = useState({ desde: "", hasta: "" });
  // B-136 — what the map chose: `{ tipo, etiqueta, dane, codigos }`, or null for every office.
  const [seleccion, setSeleccion] = useState(null);
  const mapaRef = useRef(null);
  // The committee's report: `undefined` while it is asked (the presentation waits for it, said on its
  // button), `null` for a client with none or a report that failed (no presentation), else the report.
  const [reporteComite, setReporteComite] = useState(undefined);
  const [generando, setGenerando] = useState(null);
  const [errorArchivo, setErrorArchivo] = useState(null);
  const [oficinasRegistro, setOficinasRegistro] = useState(null);
  // Office code -> its latest closing's alerts, as `faltantesDeDetalle` reads a detail; undefined
  // while the batch has not answered, null for an office it could not read.
  const [alertasPorOficina, setAlertasPorOficina] = useState({});
  // B-138 — the closings behind the figure clicked (`CierresDeLaCifra`), and the client's resumen
  // columns, so a day asked of the server reads its rows the way this page does.
  const [consulta, setConsulta] = useState(null);
  const [resumenColumnas, setResumenColumnas] = useState([]);

  const rango = useMemo(() => {
    if (preset === "mes") return mes ? { desde: `${mes}-01`, hasta: ultimoDia(mes) } : { desde: "", hasta: "" };
    return rangoDePreset(preset, hoy, personalizado);
  }, [preset, mes, personalizado, hoy]);
  // A period with both ends is asked of the server; "Todo" (or a range half typed) is not.
  const periodoCompleto = rango.desde && rango.hasta ? rango : null;

  // A new client starts on every office.
  useEffect(() => {
    setSeleccion(null);
    setReporteComite(undefined);
    setConsulta(null);
  }, [clientCode]);

  useEffect(() => {
    if (!listo) return undefined;
    let activo = true;
    setOficinasRegistro(null);
    listCierreOffices(props.token, props.mantisBusiness._id, clientCode)
      .then((r) => {
        if (activo) setOficinasRegistro(r?.success ? r.data?.offices || [] : []);
      })
      .catch(() => {
        if (activo) setOficinasRegistro([]);
      });
    return () => {
      activo = false;
    };
  }, [props.token, props.mantisBusiness._id, clientCode, listo]);

  useEffect(() => {
    // B-55 — wait for the client list: a fetch that goes out without a `clientCode` is the 400
    // a business with two cierre clients answers with.
    if (!listo) return undefined;
    let activo = true;
    setAlertasPorOficina({});
    (async () => {
      setCargando(true);
      // B-136 — a period is filtered in SQL, the way the panel does: filtering the newest 500 rows
      // here would cut a month of Bancamía (140 offices a day) short.
      const filtros = periodoCompleto ? { startDate: periodoCompleto.desde, endDate: periodoCompleto.hasta } : {};
      const [response, campos] = await Promise.all([
        filterOffices(props.token, filtros, periodoCompleto ? FILAS_PERIODO : FILAS_TODO, props.mantisBusiness._id, "brinks", clientCode).catch(() => null),
        getExtractionFields(props.token, props.mantisBusiness._id, clientCode).catch(() => null),
      ]);
      if (!activo) return;
      const resumen = columnasDeResumen(campos?.fields);
      setResumenColumnas(resumen);
      setTodasLasFilas(response?.success ? response.data.map((row) => filaDeCierre(row, resumen)) : []);
      setCargando(false);
    })();
    return () => {
      activo = false;
    };
    // The period is said by its two ends.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.token, props.mantisBusiness._id, clientCode, listo, periodoCompleto?.desde, periodoCompleto?.hasta]);

  // The period's rows (the map colours every office by them), then the map's choice.
  const filasDelPeriodo = useMemo(() => todasLasFilas.filter((c) => enRangoDeFechas(c.fecha, rango)), [todasLasFilas, rango]);
  const codigosElegidos = useMemo(() => (seleccion ? new Set(seleccion.codigos) : null), [seleccion]);
  const filas = useMemo(
    () => (codigosElegidos ? filasDelPeriodo.filter((c) => codigosElegidos.has(c.codigo)) : filasDelPeriodo),
    [filasDelPeriodo, codigosElegidos]
  );
  const [pagina, setPagina] = usePaginaPorClave(`${clientCode}|${rango.desde}|${rango.hasta}|${seleccion ? seleccion.codigos.join(",") : ""}|${todasLasFilas.length}`);

  const resumirPorOficina = (lista) => {
    const mapa = new Map();
    lista.forEach((c) => {
      const actual =
        mapa.get(c.oficinaId) || {
          id: c.oficinaId,
          nombre: c.oficina,
          codigo: c.codigo,
          // BR-38 — for an export permission granted by region.
          region: c.region !== "—" ? c.region : null,
          discrepancia: 0,
          cuadre: 0,
          total: 0,
        };
      actual.discrepancia += c.diferencia;
      actual.total += 1;
      if (c.estado === "cuadre") actual.cuadre += 1;
      mapa.set(c.oficinaId, actual);
    });
    return [...mapa.values()].sort((a, b) => a.cuadre / a.total - b.cuadre / b.total || Math.abs(b.discrepancia) - Math.abs(a.discrepancia));
  };
  const resumenDelPeriodo = useMemo(() => new Map(resumirPorOficina(filasDelPeriodo).map((o) => [o.codigo, o])), [filasDelPeriodo]);
  const oficinasResumen = useMemo(() => resumirPorOficina(filas), [filas]);

  // One `getCierreAlertsBatch` for every office's most recent closing (the batch takes 500, and a
  // client has fewer offices). A failed batch leaves each office "—", never another count.
  useEffect(() => {
    const ultimos = ultimoCierrePorOficina(todasLasFilas.map((f) => ({ codigo_oficina: f.oficinaId, fecha_cuadre: f.fecha, closing_id: f.closingId })))
      .filter((f) => f.closing_id);
    if (!ultimos.length) return undefined;
    let activo = true;
    (async () => {
      const r = await getCierreAlertsBatch(props.token, props.mantisBusiness._id, ultimos.map((f) => f.closing_id)).catch(() => null);
      if (!activo) return;
      const porCierre = new Map(r?.success ? (r.data || []).map((e) => [e.closingId, e.alerts || []]) : []);
      setAlertasPorOficina(Object.fromEntries(ultimos.map((f) => [f.codigo_oficina, porCierre.has(f.closing_id) ? { alerts: porCierre.get(f.closing_id) } : null])));
    })();
    return () => {
      activo = false;
    };
  }, [props.token, props.mantisBusiness._id, todasLasFilas]);

  const totalFaltantes = useMemo(
    () => oficinasResumen.reduce((acc, o) => acc + (faltantesDeDetalle(alertasPorOficina[o.id]) || 0), 0),
    [oficinasResumen, alertasPorOficina]
  );

  const global = useMemo(() => {
    const cuadre = filas.filter((c) => c.estado === "cuadre").length;
    return {
      cierres: filas.length,
      cuadre,
      salud: filas.length ? Math.round((cuadre / filas.length) * 100) : 0,
      enRiesgo: filas.filter((c) => c.estado !== "cuadre").length,
      discrepancia: filas.reduce((acc, c) => acc + c.diferencia, 0),
    };
  }, [filas]);

  const porCiudad = useMemo(() => agrupar(filas, (c) => c.ciudad, (c) => c.ciudad), [filas]);
  const porRegion = useMemo(() => agrupar(filas, (c) => c.region, (c) => c.region), [filas]);
  const masDiscrepancia = useMemo(
    () => [...oficinasResumen].filter((o) => o.discrepancia).sort((a, b) => Math.abs(b.discrepancia) - Math.abs(a.discrepancia)).slice(0, 10),
    [oficinasResumen]
  );

  // Per day of the period (the last 30 with closings): how much reconciled and how much was off.
  const fechas = useMemo(() => [...new Set(filas.map((c) => c.fecha).filter(Boolean))].sort().slice(-30), [filas]);
  const evolucion = fechas.map((f) => {
    const delDia = filas.filter((c) => c.fecha === f);
    return {
      fecha: f.slice(5),
      dia: f,
      discrepancia: Math.abs(delDia.reduce((a, c) => a + c.diferencia, 0)),
      salud: delDia.length ? Math.round((delDia.filter((c) => c.estado === "cuadre").length / delDia.length) * 100) : 0,
      riesgo: delDia.filter((c) => c.estado !== "cuadre").length,
    };
  });
  const tendencia = (clave) => evolucion.map((d) => ({ valor: d[clave] }));

  // B-138 — what a figure opens, and the two ways to narrow the page from what it showed.
  const ultimoPorOficina = useMemo(
    () => new Map(ultimoCierrePorOficina(todasLasFilas.map((f) => ({ codigo_oficina: f.oficinaId, fecha_cuadre: f.fecha, closing_id: f.closingId })))
      .map((f) => [f.codigo_oficina, f.closing_id])),
    [todasLasFilas]
  );
  const verOficina = (o) => setSeleccion({ tipo: "oficina", etiqueta: o.nombre, codigos: [String(o.codigo)] });
  const verPeriodo = ({ desde, hasta }) => {
    setPersonalizado({ desde, hasta });
    setPreset("personalizado");
  };
  const abrirOficina = (o) => setConsulta({
    titulo: o.nombre,
    subtitulo: `Los cierres de la oficina ${o.codigo} en el periodo.`,
    filtro: (c) => c.codigo === o.codigo,
    acciones: seleccion?.tipo === "oficina" && seleccion.codigos[0] === String(o.codigo)
      ? []
      : [{ etiqueta: "Ver solo esta oficina en el Dashboard", onClick: () => verOficina(o) }],
  });
  const abrirGrupo = (campo, nombre) => (item) => setConsulta({
    titulo: `${nombre} ${item.etiqueta}`,
    subtitulo: `${item.total} cierres, ${item.cuadre} cuadran.`,
    filtro: (c) => c[campo] === item.clave,
    acciones: [{
      etiqueta: `Ver solo ${nombre.toLowerCase()} ${item.etiqueta} en el Dashboard`,
      onClick: () => setSeleccion({
        tipo: "grupo",
        etiqueta: `${nombre} ${item.etiqueta}`,
        codigos: [...new Set(filasDelPeriodo.filter((c) => c[campo] === item.clave).map((c) => String(c.codigo)))],
      }),
    }],
  });
  const abrirDia = (dia, { delServidor = false } = {}) => setConsulta({
    titulo: `Cierres del ${dia.slice(8)}-${dia.slice(5, 7)}-${dia.slice(0, 4)}`,
    subtitulo: "Cada oficina ese día, la que más preocupa primero.",
    ...(delServidor ? { dia } : { filtro: (c) => c.fecha === dia }),
    acciones: rango.desde === dia && rango.hasta === dia
      ? []
      : [{ etiqueta: "Ver solo este día en el Dashboard", onClick: () => verPeriodo({ desde: dia, hasta: dia }) }],
  });
  const aFila = useCallback((row) => filaDeCierre(row, resumenColumnas), [resumenColumnas]);

  // The per-office report, as rows of the Excel: its own workbook for a client with no committee,
  // a sheet of the metrics workbook for one with it.
  const hojaDelReporte = () => {
    const exportables = filasExportables(acceso, clientCode, oficinasResumen, (o) => ({ oficina: o.id, region: o.region }));
    const cuerpo = exportables.map((o) => [
      o.codigo,
      o.nombre,
      o.region || "",
      o.total,
      o.cuadre,
      o.total - o.cuadre,
      o.discrepancia,
      faltantesDeDetalle(alertasPorOficina[o.id]) ?? null,
    ]);
    const suma = (i) => cuerpo.reduce((t, fila) => t + (Number(fila[i]) || 0), 0);
    const fecha = new Date().toLocaleDateString("es-CO", { timeZone: "America/Bogota" });
    return {
      nombre: "Cuadre por oficina",
      titulo: `Cuadre por oficina · ${nombreCliente}`,
      subtitulo: `Generado el ${fecha} · ${cuerpo.length} oficinas${seleccion ? ` · ${seleccion.etiqueta}` : ""}`,
      columnas: [
        { titulo: "Código", ancho: 10 },
        { titulo: "Oficina", ancho: 28 },
        { titulo: "Región", ancho: 16 },
        { titulo: "Cierres", formato: "count", ancho: 10 },
        { titulo: "Cuadrados", formato: "count", ancho: 11 },
        { titulo: "Con novedad", formato: "count", ancho: 12 },
        { titulo: "Discrepancia", formato: "money", ancho: 18 },
        { titulo: "Documentos faltantes", formato: "count", ancho: 14 },
      ],
      filas: cuerpo,
      totales: ["", "Total", "", suma(3), suma(4), suma(5), suma(6), suma(7)],
    };
  };

  const filtrarComite = (lista) => filasExportables(acceso, clientCode, lista, (o) => ({ oficina: o.officeCode, region: o.region }));
  const generar = (tipo) => {
    setErrorArchivo(null);
    setGenerando(tipo);
    const fecha = new Date().toLocaleDateString("es-CO", { timeZone: "America/Bogota" }).replace(/\//g, "-");
    const periodoDelArchivo = reporteComite
      ? (reporteComite.wholeMonth === false ? `${reporteComite.from} a ${reporteComite.to}` : reporteComite.month)
      : fecha;
    let trabajo;
    try {
      if (tipo === "pptx") trabajo = descargarPresentacion(reporteComite, { cliente: nombreCliente, filtrar: filtrarComite });
      else if (reporteComite) {
        // The office-by-day base (INFO) is the bulk of the answer, so it is asked only now.
        trabajo = conReintento(() => getCierreOperationReport(props.token, props.mantisBusiness._id, clientCode, {
          ...pedidoDelComite({ desde: reporteComite.from, hasta: reporteComite.to }, seleccion ? seleccion.codigos : null),
          includeBase: true,
        })).then((r) => {
          if (!r?.success) throw new Error(r?.message || "No se pudo leer la base de métricas");
          return descargarTablas(`Indicadores ${nombreCliente} ${periodoDelArchivo}.xlsx`, [...hojasDeMetricas(r.data, filtrarComite), hojaDelReporte()]);
        });
      } else trabajo = descargarTablas(`Reporte ${nombreCliente} ${fecha}.xlsx`, [hojaDelReporte()]);
    } catch (e) {
      trabajo = Promise.reject(e);
    }
    Promise.resolve(trabajo)
      .catch((e) => {
        console.error("No se pudo generar el archivo de Reportes", e);
        setErrorArchivo(tipo === "pptx" ? "No se pudo generar la presentación." : "No se pudo generar el Excel.");
      })
      .finally(() => setGenerando(null));
  };
  const alRecibirReporte = useCallback((r) => setReporteComite(r), []);
  const elegirPeriodo = (clave) => {
    setPreset(clave);
    if (clave === "personalizado" && !personalizado.desde) setPersonalizado({ desde: `${hoy.slice(0, 7)}-01`, hasta: hoy });
  };

  const visibles = oficinasResumen.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  return (
    <div className="brinks-scope font-redhat flex min-h-0 flex-col gap-2 bg-background p-3 sm:p-4">
      {/* Title on the left, the controls on the right at the same height: choosing "Rango" adds its
          two dates on the right, wrapping there, never pushing the controls under the title. */}
      <header className="mb-1 grid grid-cols-1 items-end gap-3 lg:grid-cols-[minmax(260px,1fr)_auto]">
        <div className="min-w-0">
          <h1 className="font-lora text-[24px] font-semibold text-brinks-foreground">Dashboard</h1>
          <p className="mt-0.5 truncate text-[13px] text-brinks-muted-foreground">Cómo está la conciliación por ciudad, zona del país y oficina</p>
        </div>
        <div {...guia("Periodo y exportación", "El periodo que cubren todas las cifras del dashboard: Todo, Hoy, 7 días, 30 días, un mes o un rango de fechas. Facturación, Tendencias y Cierres por periodo tienen sus propios controles. A la derecha, la descarga del dashboard en Excel o en presentación.")} className="flex flex-wrap items-center gap-2 lg:justify-end lg:justify-self-end">
          <div className="flex flex-wrap items-center gap-0.5 rounded-[9px] border bg-card p-1 shadow-brinks-card" role="group" aria-label="Periodo">
            {PERIODOS.map((p) => (
              <button
                key={p.clave}
                type="button"
                onClick={() => elegirPeriodo(p.clave)}
                aria-pressed={preset === p.clave}
                className={cn(
                  "rounded-[7px] px-3 py-1.5 text-[12.5px] font-medium transition",
                  preset === p.clave ? "bg-primary text-primary-foreground" : "text-brinks-muted-foreground hover:text-brinks-foreground"
                )}
              >
                {p.etiqueta}
              </button>
            ))}
          </div>
          {preset === "mes" ? (
            <input
              type="month"
              aria-label="Mes"
              className="h-9 rounded-[8px] border bg-card px-2 text-[12.5px] text-brinks-foreground shadow-brinks-card"
              value={mes}
              max={hoy.slice(0, 7)}
              onChange={(e) => e.target.value && setMes(e.target.value)}
            />
          ) : null}
          {preset === "personalizado" ? (
            <span className="flex items-center gap-1.5 text-[12px] text-brinks-muted-foreground">
              <input
                type="date"
                aria-label="Desde"
                className="h-9 rounded-[8px] border bg-card px-2 text-[12.5px] text-brinks-foreground shadow-brinks-card"
                value={personalizado.desde}
                max={hoy}
                onChange={(e) => setPersonalizado((p) => ({ ...p, desde: e.target.value }))}
              />
              a
              <input
                type="date"
                aria-label="Hasta"
                className="h-9 rounded-[8px] border bg-card px-2 text-[12.5px] text-brinks-foreground shadow-brinks-card"
                value={personalizado.hasta}
                max={hoy}
                onChange={(e) => setPersonalizado((p) => ({ ...p, hasta: e.target.value }))}
              />
            </span>
          ) : null}
          {puedeExportar ? (
            <>
              <Button size="sm" variant={reporteComite ? "outline" : "default"} className="h-9 gap-1.5" disabled={Boolean(generando)} onClick={() => generar("excel")}>
                <Download className="h-4 w-4 shrink-0" />
                <span className="truncate">{generando === "excel" ? "Generando…" : reporteComite ? "Excel de métricas" : "Exportar Excel"}</span>
              </Button>
              {reporteComite !== null ? (
                <Button
                  size="sm"
                  className="h-9 gap-1.5"
                  disabled={!reporteComite || Boolean(generando)}
                  title={reporteComite ? undefined : "La presentación se arma con el comité de seguimiento, que se está leyendo"}
                  onClick={() => generar("pptx")}
                >
                  {reporteComite ? <Presentation className="h-4 w-4 shrink-0" /> : <Loader2 className="h-4 w-4 shrink-0 animate-spin" />}
                  <span className="truncate">{generando === "pptx" ? "Generando…" : reporteComite ? "Presentación" : "Cargando métricas…"}</span>
                </Button>
              ) : null}
            </>
          ) : null}
        </div>
      </header>

      {errorArchivo ? <p role="alert" className="rounded-[8px] border border-brinks-danger-border bg-brinks-danger-soft px-3 py-2 text-[12px] text-brinks-danger">{errorArchivo}</p> : null}

      {/* B-136 — the map first: it is the page's filter. It needs only the client's offices
          (`listCierreOffices`, under a second), so it is drawn at once and its points take their
          colour when the period's rows arrive. */}
      {listo && (oficinasRegistro === null || oficinasRegistro.length) ? (
        <div {...guia("Mapa de oficinas", "Cada punto es un municipio con oficinas: su tamaño dice cuántas oficinas tiene y su color qué parte de sus cierres del periodo cuadra (verde cuadra, rojo no). Encima, el resumen: oficinas, municipios, % que cuadra y en riesgo. Toca un punto para ver sus cifras y filtrar toda la página por ese municipio; la lista de al lado ordena los municipios por atender, el peor primero.")} ref={mapaRef} className="scroll-mt-4">
        <MapaOficinas
          oficinas={oficinasRegistro || []}
          resumen={resumenDelPeriodo}
          cliente={nombreCliente}
          cargando={oficinasRegistro === null}
          calculando={cargando}
          seleccion={seleccion}
          onSeleccion={setSeleccion}
        />
        </div>
      ) : null}

      {/* B-136 — what the map chose, floating over the page wherever it is scrolled to, so going back
          to every office never needs the map in view. */}
      {seleccion ? (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 z-[1100] flex max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-1 rounded-full border border-white/10 bg-primary py-1.5 pl-4 pr-1.5 text-[12.5px] text-primary-foreground shadow-[0_18px_40px_-12px_rgba(15,23,42,0.55)] animate-in fade-in slide-in-from-bottom-4"
        >
          <MapPin className="h-4 w-4 shrink-0 text-white/70" />
          <span className="min-w-0 truncate pl-1.5">
            <span className="text-white/60">Mostrando solo </span>
            <span className="font-semibold">{seleccion.etiqueta}</span>
            <span className="text-white/60">
              {seleccion.tipo !== "oficina" ? ` · ${seleccion.codigos.length} ${seleccion.codigos.length === 1 ? "oficina" : "oficinas"}` : " · una oficina"}
            </span>
          </span>
          <span className="mx-1.5 h-5 w-px shrink-0 bg-white/15" />
          <button
            type="button"
            onClick={() => mapaRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="hidden shrink-0 rounded-full px-3 py-1.5 text-[12px] font-medium text-white/80 transition hover:bg-white/10 hover:text-white sm:inline"
          >
            Ver en el mapa
          </button>
          <button
            type="button"
            onClick={() => setSeleccion(null)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-[12px] font-semibold text-primary transition hover:bg-white/90"
          >
            <X className="h-3.5 w-3.5" /> Ver todas las oficinas
          </button>
        </div>
      ) : null}

      {cargando ? (
        <div className="grid place-items-center rounded-[10px] border border-dashed bg-card py-14">
          <Spinner size="lg" />
        </div>
      ) : !filas.length ? (
        <div className="grid place-items-center rounded-[10px] border border-dashed bg-card py-14 text-center">
          <div>
            <p className="text-[13px] font-medium text-brinks-foreground">
              {todasLasFilas.length ? "Sin cierres en este periodo" : "Sin cierres todavía"}
            </p>
            <p className="text-[12px] text-brinks-muted-foreground">
              {todasLasFilas.length ? "Elige otro periodo u otra oficina." : "Este reporte se llena a medida que corren cierres reales."}
            </p>
          </div>
        </div>
      ) : (
        <section {...guia("Indicadores", "Las cuatro cifras principales del periodo. A continuación te explicamos cada una.")} className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
          <Indicador
            hero
            icono={ShieldCheck}
            etiqueta="Salud del cuadre"
            valor={`${global.salud}%`}
            detalle={`${global.cuadre} de ${global.cierres} cierres cuadran`}
            tendencia={tendencia("salud")}
            onAbrir={() => setConsulta({ titulo: "Cierres del periodo", subtitulo: `${global.cuadre} de ${global.cierres} cuadran.` })}
          />
          <Indicador
            icono={ShieldAlert}
            etiqueta="Cierres en riesgo"
            valor={String(global.enRiesgo)}
            detalle="Con alerta o por revisar"
            tendencia={tendencia("riesgo")}
            tono="bg-brinks-warning-soft text-brinks-warning"
            onAbrir={() => setConsulta({ titulo: "Cierres en riesgo", subtitulo: "Con alerta o por revisar.", filtro: (c) => c.estado !== "cuadre" })}
          />
          <Indicador
            icono={Scale}
            etiqueta="Discrepancia"
            valor={moneda(global.discrepancia)}
            detalle="Suma de las diferencias del periodo"
            tendencia={tendencia("discrepancia")}
            tono="bg-brinks-surface text-brinks-foreground"
            onAbrir={() => setConsulta({ titulo: "Cierres con discrepancia", subtitulo: "Los que dejaron una diferencia, la mayor primero.", filtro: (c) => Boolean(c.diferencia) })}
          />
          <Indicador
            icono={FileX2}
            etiqueta="Docs. faltantes"
            valor={String(totalFaltantes)}
            detalle="En el último cierre de cada oficina"
            tono="bg-brinks-danger-soft text-brinks-danger"
            onAbrir={totalFaltantes ? () => setConsulta({
              titulo: "Documentos faltantes",
              subtitulo: "El último cierre de cada oficina al que le falta algún documento.",
              filtro: (c) => c.closingId && ultimoPorOficina.get(c.oficinaId) === c.closingId
                && (faltantesDeDetalle(alertasPorOficina[c.oficinaId]) || 0) > 0,
            }) : undefined}
          />
        </section>
      )}

      {/* B-136 — the client's monthly follow-up committee (Bancamía); nothing for a client without one.
          It follows the page's period and the map's offices; `key` drops the previous client's. */}
      {listo ? (
        <ComiteSeguimiento
          key={`comite-${clientCode || "-"}`}
          token={props.token}
          mantisBusinessId={props.mantisBusiness._id}
          clientCode={clientCode}
          periodo={periodoCompleto}
          oficinas={seleccion ? seleccion.codigos : null}
          onReporte={alRecibirReporte}
          onElegir={setSeleccion}
          onAbrirDia={(dia) => abrirDia(dia, { delServidor: true })}
        />
      ) : null}

      {!cargando && filas.length ? (
        <>
          <div className="grid gap-2 xl:grid-cols-2">
            <Grafica icono={LineIcon} titulo="Evolución diaria de la discrepancia" subtitulo="Cada día del periodo con cierres (los últimos 30). Toca un día para ver sus cierres.">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={evolucion}
                    margin={{ top: 6, right: 6, bottom: 0, left: 0 }}
                    style={{ cursor: "pointer" }}
                    onClick={(e) => {
                      const d = e?.activePayload?.[0]?.payload;
                      if (d?.dia) abrirDia(d.dia);
                    }}
                  >
                    <defs>
                      <linearGradient id="evolucion-discrepancia" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--brinks-danger)" stopOpacity={0.22} />
                        <stop offset="100%" stopColor="var(--brinks-danger)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="var(--brinks-border)" vertical={false} />
                    <XAxis dataKey="fecha" tickLine={false} axisLine={false} fontSize={11} />
                    <YAxis tickFormatter={(v) => `${Math.round(Number(v) / 1000)}K`} tickLine={false} axisLine={false} fontSize={11} width={40} />
                    <Tooltip formatter={(v) => moneda(Number(v))} />
                    <Area type="monotone" dataKey="discrepancia" name="Discrepancia" stroke="var(--brinks-danger)" strokeWidth={2.2} fill="url(#evolucion-discrepancia)" activeDot={{ r: 5 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Grafica>

            <Grafica
              icono={Scale}
              titulo="Oficinas con más discrepancia"
              subtitulo="Las diez que más pesos dejaron sin cuadrar en el periodo. Toca una para ver sus cierres."
              cuerpo={
                masDiscrepancia.length ? (
                  <table className="w-full text-[12.5px]">
                    <thead>
                      <tr className="border-b text-left text-[10.5px] uppercase tracking-wide text-brinks-muted-foreground">
                        <th className="px-4 py-2 font-medium">Oficina</th>
                        <th className="px-4 py-2 text-right font-medium">Cierres</th>
                        <th className="px-4 py-2 text-right font-medium">Cuadra</th>
                        <th className="px-4 py-2 text-right font-medium">Discrepancia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {masDiscrepancia.map((o, i) => (
                        <tr key={o.id} {...filaQueAbre(() => abrirOficina(o), o.nombre)} className={cn("border-b last:border-0", FILA_QUE_ABRE)}>
                          <td className="px-4 py-2">
                            <span className="mr-2 inline-block w-4 text-right tabular-nums text-brinks-muted-foreground">{i + 1}</span>
                            {o.nombre}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">{o.total}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-brinks-muted-foreground">{o.total ? Math.round((o.cuadre / o.total) * 100) : 0}%</td>
                          <td className="px-4 py-2 text-right font-semibold tabular-nums text-brinks-danger">{moneda(o.discrepancia)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="px-4 py-10 text-center text-[12px] text-brinks-muted-foreground">Ninguna oficina tuvo discrepancia en el periodo.</p>
                )
              }
            />
          </div>

          <div className="grid items-start gap-2 xl:grid-cols-2">
            <Grafica icono={Building2} titulo="Conciliación por ciudad" subtitulo="Qué parte de los cierres de cada ciudad cuadra. Toca una para ver sus cierres." extra={<Conteo n={porCiudad.length} />} cuerpo={<ListaDeGrupos grupos={porCiudad} onAbrir={abrirGrupo("ciudad", "Ciudad")} />} />
            <Grafica icono={MapIcon} titulo="Zonas del país" subtitulo="La misma lectura, por región." extra={<Conteo n={porRegion.length} />} cuerpo={<ListaDeGrupos grupos={porRegion} onAbrir={abrirGrupo("region", "Zona")} />} />
          </div>

          <Grafica
            icono={Scale}
            titulo="Detalle por oficina"
            subtitulo="Cierres, novedades y discrepancia de cada oficina en el periodo, la que menos cuadra primero. Toca una para ver sus cierres."
            extra={<Conteo n={oficinasResumen.length} />}
            cuerpo={
              <div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px] text-sm">
                    <thead className="bg-brinks-surface shadow-[inset_0_-1px_0_var(--brinks-border)]">
                      <tr className="text-left text-[11px] uppercase tracking-wide text-brinks-muted-foreground">
                        <th className="px-4 py-2.5 font-semibold">Oficina</th>
                        <th className="px-4 py-2.5 text-right font-semibold">Cierres</th>
                        <th className="px-4 py-2.5 text-right font-semibold">Con novedad</th>
                        <th className="px-4 py-2.5 text-right font-semibold">Docs. faltantes</th>
                        <th className="px-4 py-2.5 text-right font-semibold">Discrepancia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibles.map((o) => {
                        const n = o.id in alertasPorOficina ? faltantesDeDetalle(alertasPorOficina[o.id]) : undefined;
                        return (
                          <tr key={o.id} {...filaQueAbre(() => abrirOficina(o), o.nombre)} className={cn("border-b text-brinks-foreground transition last:border-0", FILA_QUE_ABRE)}>
                            <td className="px-4 py-2.5">{o.nombre}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{o.total}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{o.total - o.cuadre}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-brinks-muted-foreground">{n === undefined ? "…" : n === null ? "—" : n}</td>
                            <td className={cn("px-4 py-2.5 text-right font-semibold tabular-nums", o.discrepancia !== 0 && "text-brinks-danger")}>{moneda(o.discrepancia)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagina pagina={pagina} total={oficinasResumen.length} onPagina={setPagina} />
              </div>
            }
          />
        </>
      ) : null}

      {/* "Cierres por periodo": closings per week, month and year, by how they stand — every office,
          with its own controls; `key` drops everything of the previous client. */}
      {listo ? (
        <VolumenCierres key={`volumen-${clientCode || "-"}`} token={props.token} mantisBusinessId={props.mantisBusiness._id} clientCode={clientCode} onPeriodo={verPeriodo} />
      ) : null}

      {/* B-154 — the bank's transactions and the reversals since January 2024; nothing for a client without them. */}
      {listo ? (
        <TransaccionesReversas
          key={`transacciones-${clientCode || "-"}`}
          token={props.token}
          mantisBusinessId={props.mantisBusiness._id}
          clientCode={clientCode}
          puedeExportar={puedeExportar}
        />
      ) : null}

      {/* B-105 — what is billed to the bank; nothing for a client that bills nothing. */}
      {listo ? (
        <FacturacionBanco token={props.token} mantisBusinessId={props.mantisBusiness._id} clientCode={clientCode} puedeExportar={puedeExportar} />
      ) : null}

      {/* B-162 — the close over a range, as the operation reports it; nothing for a client without a day reconciliation. */}
      {listo ? (
        <CierreConciliacion
          key={`conciliacion-${clientCode || "-"}`}
          token={props.token}
          mantisBusinessId={props.mantisBusiness._id}
          clientCode={clientCode}
          puedeExportar={puedeExportar}
        />
      ) : null}

      {/* B-109 — the month's trends; nothing for a client without a day reconciliation. */}
      {listo ? (
        <TendenciasConciliacion
          key={`tendencias-${clientCode || "-"}`}
          token={props.token}
          mantisBusinessId={props.mantisBusiness._id}
          clientCode={clientCode}
          onElegirOficina={(o) => verOficina({ codigo: o.codigo, nombre: o.oficina })}
        />
      ) : null}

      <CierresDeLaCifra
        consulta={consulta}
        onClose={() => setConsulta(null)}
        filas={filas}
        aFila={aFila}
        codigos={codigosElegidos}
        token={props.token}
        mantisBusinessId={props.mantisBusiness._id}
        clientCode={clientCode}
      />
    </div>
  );
}

// B-139 — two tabs: "Cliente" (the dashboard above, what the client is shown and presented) and
// "Equipo" (Brinks' own team: queue, people, response times, each day's flow), offered only with
// `cierres.team.view` on the whole client. The Cliente tab stays mounted while hidden, so coming
// back does not read its rows again.
const PESTANAS = [
  { clave: "cliente", etiqueta: "Cliente" },
  { clave: "equipo", etiqueta: "Equipo" },
];

function ReportesConPestanas(props) {
  const { clientCode, listo, clientes } = useClienteCierres();
  const acceso = useAccesoCierres();
  const verEquipo = listo && Boolean(clientCode) && puede(acceso, PERMISO.VER_EQUIPO, { cliente: clientCode, entero: true });
  const [pestana, setPestana] = useState("cliente");
  const activa = verEquipo ? pestana : "cliente";

  if (!verEquipo) return <CierresReportes {...props} />;
  return (
    <div className="brinks-scope font-redhat flex min-h-0 flex-col bg-background">
      <div className="px-3 pt-3 sm:px-4 sm:pt-4">
        <div className="inline-flex items-center gap-0.5 rounded-[10px] border bg-card p-1 shadow-brinks-card" {...guia("Cliente y Equipo", "\"Cliente\" es lo que se le muestra al cliente; \"Equipo\" es el trabajo de tu equipo sobre ese cliente.")} role="tablist" aria-label="Tableros de Reportes">
          {PESTANAS.map((t) => (
            <button
              key={t.clave}
              type="button"
              role="tab"
              aria-selected={activa === t.clave}
              onClick={() => setPestana(t.clave)}
              className={cn(
                "rounded-[8px] px-4 py-1.5 text-[13px] font-medium transition",
                activa === t.clave ? "bg-primary text-primary-foreground" : "text-brinks-muted-foreground hover:text-brinks-foreground"
              )}
            >
              {t.etiqueta}
            </button>
          ))}
        </div>
      </div>
      <div hidden={activa !== "cliente"}>
        <CierresReportes {...props} />
      </div>
      {activa === "equipo" ? (
        <div className="p-3 sm:p-4">
          <EquipoCierres
            key={`equipo-${clientCode}`}
            token={props.token}
            mantisBusinessId={props.mantisBusiness._id}
            clientCode={clientCode}
            nombreCliente={(clientes || []).find((c) => c.code === clientCode)?.name || clientCode}
            puedeExportar={puede(acceso, PERMISO.EXPORTAR, { cliente: clientCode })}
          />
        </div>
      ) : null}
    </div>
  );
}

export default connect(selectAuthState)(ReportesConPestanas);
